import { forwardRef } from 'react';
import type { ChangeEvent } from 'react';
import { InputField, type InputFieldProps } from './Input';
import saFlagSrc from '../../public/images/saFlag.svg';

export const SAEE_SAUDI_COUNTRY_CODE = '+966' as const;

export const SAEE_SAUDI_PHONE_PLACEHOLDER = '5XX XXX XXXX';

export type SaeeSaudiPhoneInputFieldProps = Omit<InputFieldProps, 'leftAddon' | 'type' | 'inputMode' | 'contentDir'> & {
  /** Strip non-digits and cap at 9 (national mobile without country code). Default true. */
  normalizeNationalDigits?: boolean;
  countryCode?: string;
};

function nationalDigitsOnly(raw: string): string {
  return raw.replace(/\D/g, '').slice(0, 9);
}

/**
 * Saee-themed Saudi mobile field: static +966, flag, LTR content, brokerage/broker UI parity.
 */
export const SaeeSaudiPhoneInputField = forwardRef<HTMLInputElement, SaeeSaudiPhoneInputFieldProps>(
  function SaeeSaudiPhoneInputField(
    {
      normalizeNationalDigits = true,
      countryCode = SAEE_SAUDI_COUNTRY_CODE,
      placeholder = SAEE_SAUDI_PHONE_PLACEHOLDER,
      onChange,
      className,
      ...rest
    },
    ref,
  ) {
    const leftAddon = (
      <span className="inline-flex items-center gap-1 text-base font-normal text-gray-900">
        <img src={saFlagSrc} alt="" width={21} height={15} className="shrink-0 object-contain" aria-hidden />
        <span>{countryCode}</span>
      </span>
    );

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      if (!onChange) return;
      const next = normalizeNationalDigits ? nationalDigitsOnly(e.target.value) : e.target.value;
      onChange({ ...e, target: { ...e.target, value: next } } as ChangeEvent<HTMLInputElement>);
    };

    return (
      <InputField
        ref={ref}
        {...rest}
        className={[className, 'force-ltr'].filter(Boolean).join(' ')}
        type="tel"
        inputMode="tel"
        contentDir="ltr"
        leftAddon={leftAddon}
        placeholder={placeholder}
        onChange={handleChange}
      />
    );
  },
);
